/**
 * Display text composed from structured values plus the labels `GET /meta` supplies.
 *
 * The server sends no prose: no `calculation`, no `resultLabel`, no `chronology` string. What it
 * sends is the trigger, the period and the shape, and the sentence is assembled here — which is
 * presentation, not derivation. Nothing in this file decides a date, a status or a validity.
 *
 * Labels are always looked up from `/meta`. There is no hardcoded map of vocabulary spellings,
 * because the database's own CHECK lists are the authority and a map here would drift from them.
 */

import { fmtPeriod, lowerLabel } from '~/lib/format';
import type { Meta, RuleForFiling, RuleVersion, VocabularyTerm } from '~/api/schema';

export interface Vocabulary {
  triggerLabel: (code: string) => string;
  shapeLabel: (code: string) => string;
  unitLabels: Readonly<Record<string, string>>;
  fieldLabel: (code: string) => string;
  matterLabel: (code: string) => string;
}

const labelFrom = (terms: readonly VocabularyTerm[]) => (code: string) =>
  terms.find((term) => term.code === code)?.label ?? code;

export function vocabularyOf(meta: Meta | undefined): Vocabulary {
  const triggers = meta?.triggers ?? [];
  const shapes = meta?.ruleShapes ?? [];
  const matters = meta?.matters ?? [];
  const units = meta?.periodUnits ?? [];
  const fields = meta?.fieldLabels ?? {};

  return {
    triggerLabel: labelFrom(triggers),
    shapeLabel: labelFrom(shapes),
    matterLabel: labelFrom(matters),
    unitLabels: Object.fromEntries(units.map((unit) => [unit.code, unit.label])),
    fieldLabel: (code) => fields[code] ?? code,
  };
}

/** One line saying what the governing version calculates. */
export function describeVersion(version: RuleVersion, shape: string, vocab: Vocabulary): string {
  const from = lowerLabel(vocab.triggerLabel(version.trigger));

  if (shape === 'term') {
    const first = fmtPeriod(version.firstTerm, vocab.unitLabels);
    const later = fmtPeriod(version.laterTerm, vocab.unitLabels);
    if (version.laterTerm) {
      return `A first term of ${first} from ${from}, renewable for ${later} at a time.`;
    }
    return `A term of ${first} from ${from}.`;
  }

  if (shape === 'annuity') {
    const max = fmtPeriod(version.maxTerm, vocab.unitLabels);
    // A schedule replaces the "every year from year N" sentence rather than decorating it: the
    // interval changes partway, so "every year" would be wrong about most of the series.
    if (version.schedule) {
      return `A maximum term of ${max} from ${from}, paid on the schedule below.`;
    }
    const year = version.firstAnnuityYear;
    return `A maximum term of ${max} from ${from}, with an annuity every year from year ${year ?? '—'}.`;
  }

  const period = fmtPeriod(version.period, vocab.unitLabels);
  return `${period} from ${from}.`;
}

/** The small label above a rule card's date: "Term expires", "Falls due". */
export function resultLabelFor(shape: string): string {
  if (shape === 'term') return 'Term expires';
  if (shape === 'annuity') return 'Term ends';
  return 'Falls due';
}

export function governingVersionOf(rule: RuleForFiling): RuleVersion | null {
  if (rule.governingVersionId) {
    return rule.versions.find((version) => version.id === rule.governingVersionId) ?? null;
  }
  if (rule.governingVersion) {
    return rule.versions[rule.governingVersion - 1] ?? null;
  }
  return null;
}

/** The version in force today — frequently not the one governing the filing on screen. */
export function liveVersionOf(rule: RuleForFiling): RuleVersion | null {
  return rule.versions.find((version) => version.inForce) ?? null;
}


/**
 * A payment schedule in words, block by block.
 *
 * The blocks alone do not tell a reader when anything falls due — the numbering, the pin and the
 * offset interact — so each is described rather than tabulated, and the numbering is stated
 * because it is the statute's and not a position in a list.
 */
export function describeSchedule(
  schedule: NonNullable<RuleVersion['schedule']>,
  vocab: Vocabulary,
): string[] {
  const lines = schedule.blocks.map((block) => {
    const span =
      block.coversFrom === block.coversTo
        ? `Period ${block.coversFrom}`
        : `Periods ${block.coversFrom} to ${block.coversTo}`;
    const every =
      block.interval === 1
        ? `every ${lowerLabel(vocab.unitLabels[block.unit] ?? block.unit)}`
        : `every ${block.interval} ${lowerLabel(vocab.unitLabels[block.unit] ?? block.unit)}s`;
    const pinned =
      block.pin === 'month_end'
        ? ', on the last day of the month'
        : block.pin === 'month_start'
          ? ', on the first day of the month'
          : block.pin === 'fixed_day' && block.pinDay
            ? `, on day ${block.pinDay}${block.pinMonth ? ` of month ${block.pinMonth}` : ''}`
            : '';
    return `${span}: ${every}${pinned}.`;
  });

  if (schedule.gateTrigger && schedule.gatePeriod) {
    lines.unshift(
      `The first payment falls ${schedule.gatePeriod.value} ${lowerLabel(
        vocab.unitLabels[schedule.gatePeriod.unit] ?? schedule.gatePeriod.unit,
      )}(s) after ${lowerLabel(vocab.triggerLabel(schedule.gateTrigger))}.`,
    );
  }
  if (schedule.offsetValue) {
    const direction = schedule.offsetValue < 0 ? 'before' : 'after';
    lines.push(
      `Every payment falls ${Math.abs(schedule.offsetValue)} ${schedule.offsetUnit ?? 'day'}(s) ${direction} that date.`,
    );
  }
  if (schedule.maxCount) {
    lines.push(`The series ends after period ${schedule.maxCount}.`);
  }
  return lines;
}
